import { Component, OnInit, ViewChild, TemplateRef } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { LocalStorage } from "ngx-webstorage";
import { ProfileService } from "src/app/add-member-group/services/profile.service";
import { AuthService } from "src/app/auth/services/auth.service";
import { CommonService } from "src/app/shared/services/common.service";

@Component({
  selector: "app-my-account",
  templateUrl: "./my-account.component.html",
  styleUrls: ["./my-account.component.scss"],
})
export class MyAccountComponent implements OnInit {
  public userData: any;
  public profileImg: "assets/images/dashboard/profile.jpg";
  
  @LocalStorage()
  public userDetails;
  profileimg:any;

  // Password form & visibility controls
  changePasswordForm: FormGroup;
  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  
  // Password strength and validation properties
  passwordStrength: 'weak' | 'medium' | 'strong' = 'weak';
  hasUpperCase = false;
  hasLowerCase = false;
  hasNumber = false;
  hasSpecialChar = false;
  passwordsMatch = false;
  
  @ViewChild('changePasswordModal') changePasswordModal: TemplateRef<any>;
  
  constructor(
    public authService: AuthService,
    private profileService: ProfileService,
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private modalService: NgbModal
  ) {
    this.getUser();
    this.initChangePasswordForm();
  }

  initChangePasswordForm() {
    this.changePasswordForm = this.formBuilder.group({
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
    
    // Monitor password changes to update strength indicators
    this.changePasswordForm.get('newPassword').valueChanges.subscribe(value => {
      this.checkPasswordStrength(value);
    });
    
    // Monitor confirm password to show match status
    this.changePasswordForm.valueChanges.subscribe(() => {
      const newPassword = this.changePasswordForm.get('newPassword').value;
      const confirmPassword = this.changePasswordForm.get('confirmPassword').value;
      
      if (newPassword && confirmPassword) {
        this.passwordsMatch = newPassword === confirmPassword;
      } else {
        this.passwordsMatch = false;
      }
    });
  }
  
  checkPasswordStrength(password: string) {
    if (!password) {
      this.passwordStrength = 'weak';
      this.hasUpperCase = false;
      this.hasLowerCase = false;
      this.hasNumber = false;
      this.hasSpecialChar = false;
      return;
    }
    
    // Check for various character types
    this.hasUpperCase = /[A-Z]/.test(password);
    this.hasLowerCase = /[a-z]/.test(password);
    this.hasNumber = /[0-9]/.test(password);
    this.hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    // Count the criteria that are met
    let strengthScore = 0;
    if (password.length >= 8) strengthScore++;
    if (this.hasUpperCase) strengthScore++;
    if (this.hasLowerCase) strengthScore++;
    if (this.hasNumber) strengthScore++;
    if (this.hasSpecialChar) strengthScore++;
    
    // Determine overall strength
    if (strengthScore <= 2) {
      this.passwordStrength = 'weak';
    } else if (strengthScore <= 4) {
      this.passwordStrength = 'medium';
    } else {
      this.passwordStrength = 'strong';
    }
  }
  getUserGroupName(): string | undefined {
    const userGroups = this.userDetails?.userGroup;
    if (!userGroups || userGroups.length === 0) {
      return undefined;
    }
  
    const commonGroup = userGroups.find(group => group.IsCommon === 1);
    return commonGroup?.Name ?? userGroups[0]?.Name;
  }
  
  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    
    if (newPassword === confirmPassword) {
      return null;
    }
    
    return { passwordMismatch: true };
  }

  getUser() {
    this.userData = this.userDetails;
    console.log("user data ",this.userData)
    this.profileimg =localStorage.getItem('profileImg');
  }

  ngOnInit() {
    this.getProfileData();
  }
  
  getProfileData() {
    this.profileService.getProfileData(this.userDetails?.userID)
    .subscribe((res: any) => {
      if (res) {
        this.profileImg = res.data?.profile_image
      }
    });
  }

  togglePasswordVisibility(field: string) {
    if (field === 'old') {
      this.showOldPassword = !this.showOldPassword;
    } else if (field === 'new') {
      this.showNewPassword = !this.showNewPassword;
    } else if (field === 'confirm') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  openChangePasswordModal() {
    // Reset form and strength indicators when opening modal
    this.changePasswordForm.reset();
    this.passwordStrength = 'weak';
    this.hasUpperCase = false;
    this.hasLowerCase = false;
    this.hasNumber = false;
    this.hasSpecialChar = false;
    this.passwordsMatch = false;
    
    this.modalService.open(this.changePasswordModal, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg' // Make the modal a bit larger for better readability
    });
  }

  async changePassword() {
    if (this.changePasswordForm.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.changePasswordForm.controls).forEach(key => {
        this.changePasswordForm.get(key).markAsTouched();
      });
      return;
    }

    try {
      const formData = this.changePasswordForm.value;
      
      // Frontend validation for matching passwords
      if (formData.newPassword !== formData.confirmPassword) {
        this.commonService.showToast("error", "Error", "New password and confirm password do not match");
        return;
      }

      // Call auth service to change password
      const success = await this.authService.changePassword({
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword
      });

      if (success) {
        this.commonService.showToast("success", "Success", "Password changed successfully. Please login again.");
        this.modalService.dismissAll();
        // Logout will be handled by the service after successful password change
      } else {
        this.commonService.showToast("error", "Error", "Failed to change password. Please try again.");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      this.commonService.showToast("error", "Error", "An error occurred while changing password.");
    }
  }

  logoutFunc() {
    this.authService.logout()
  }
}